import { AuthService } from '@auth0/auth0-angular';
import { GetTokenSilentlyVerboseResponse } from '@auth0/auth0-spa-js';

import { inject, Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { ApContentService } from '@ap/content';

import content from './mijn-klant-login.content.json';

@Injectable({ providedIn: 'root' })
export class MijnAuthSessieManagerService {
    private readonly auth = inject(AuthService);
    private readonly contentService = inject(ApContentService);

    public readonly isAuthenticated$ = this.auth.isAuthenticated$;
    public readonly user$ = this.auth.user$;
    public readonly appState$ = this.auth.appState$;
    public readonly idTokenClaims$ = this.auth.idTokenClaims$;

    public constructor() {
        this.contentService.addContentItems(content);
    }

    public login(): Observable<void> {
        return this.auth.loginWithRedirect({
            appState: { returnTo: this.getReturnTo() },
            authorizationParams: {
                redirect_uri: this.getLoginRedirectUri()
            }
        });
    }

    public loginWithEmail(): Observable<void> {
        return this.auth.loginWithRedirect({
            appState: { returnTo: this.getReturnTo() },
            authorizationParams: {
                redirect_uri: this.getLoginRedirectUri(),
                connection: 'email',
                prompt: 'login'
            }
        });
    }

    public signup(returnTo: string, loginHint?: string): Observable<void> {
        return this.auth.loginWithRedirect({
            appState: { returnTo },
            authorizationParams: {
                redirect_uri: this.getLoginRedirectUri(),
                prompt: 'login',
                screen_hint: 'signup',
                login_hint: loginHint
            }
        });
    }

    public logout(): Observable<void> {
        return this.auth.logout({ logoutParams: { returnTo: this.getLogoutReturnUrl() } });
    }

    public getAccessTokenSilently(detailedResponse?: boolean): Observable<string | GetTokenSilentlyVerboseResponse> {
        return this.auth.getAccessTokenSilently({ detailedResponse } as any);
    }

    public refreshAccessToken(): Observable<string | GetTokenSilentlyVerboseResponse> {
        return this.auth.getAccessTokenSilently({ cacheMode: 'off' } as any);
    }

    public terminateSession(): Observable<void> {
        return this.isAuthenticated$.pipe(
            take(1),
            switchMap((authenticated) => (authenticated ? this.logout() : EMPTY))
        );
    }

    private getReturnTo(): string {
        const orgUrl = new URLSearchParams(window.location.search).get('orgUrl');
        return orgUrl ?? this.contentService.get('mijn.klant.login.default.return.to');
    }

    private getLoginRedirectUri(): string {
        const origin = window.location.origin;
        const loginLink =
            origin.indexOf('localhost') > -1 ? '/mijn-login/auth0inloggen' : this.contentService.get('mijn.klant.login.redirect.uri');
        return `${origin}${loginLink}`;
    }

    private getLogoutReturnUrl(): string {
        const origin = window.location.origin;
        const loginLink =
            origin.indexOf('localhost') > -1 ? '/mijn-login/auth0inloggen' : this.contentService.get('mijn.klant.logout.return.url');
        return `${origin}${loginLink}`;
    }
}
